import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

const EC2_URL = 'https://api.algrok.com';

const LiveDataContext = createContext({});

export function LiveDataProvider({ children }) {
  const [ticks, setTicks] = useState({});
  const [connected, setConnected] = useState(false);
  const intervalRef = useRef(null);
  const subscribedTokens = useRef(new Set());
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  /* ── HTTP Polling fallback (always runs) ─────────── */
  const fetchTicks = useCallback(async () => {
    try {
      const r = await fetch(EC2_URL + '/ticks', { signal: AbortSignal.timeout(4000) });
      if (!r.ok) { setConnected(false); return; }
      const d = await r.json();
      // Merge tick data keyed by instrument_token AND tradingsymbol
      setTicks(prev => {
        const merged = { ...prev };
        if (typeof d === 'object' && !Array.isArray(d)) {
          Object.entries(d).forEach(([key, val]) => {
            merged[key] = { ...merged[key], ...val };
            // Also key by instrument_token if present
            if (val?.instrument_token) {
              merged[val.instrument_token] = { ...merged[val.instrument_token], ...val };
            }
          });
        } else if (Array.isArray(d)) {
          d.forEach(t => {
            if (t.instrument_token) {
              merged[t.instrument_token] = { ...merged[t.instrument_token], ...t };
            }
            if (t.tradingsymbol) {
              merged[t.tradingsymbol] = { ...merged[t.tradingsymbol], ...t };
            }
          });
        }
        return merged;
      });
      setConnected(true);
    } catch {
      setConnected(false);
    }
  }, []);

  useEffect(() => {
    fetchTicks();
    intervalRef.current = setInterval(fetchTicks, 1000);
    return () => clearInterval(intervalRef.current);
  }, [fetchTicks]);

  /* ── WebSocket connection (if EC2 supports it) ───── */
  const connectWS = useCallback(() => {
    // Try WebSocket endpoint - if EC2 has ws support
    const wsUrl = EC2_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/ws';
    try {
      const ws = new WebSocket(wsUrl);
      ws.onopen = () => {
        wsRef.current = ws;
        setConnected(true);
        // Re-subscribe existing tokens
        if (subscribedTokens.current.size > 0) {
          ws.send(JSON.stringify({
            type: 'subscribe',
            tokens: Array.from(subscribedTokens.current),
          }));
        }
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'tick' && data.data) {
            setTicks(prev => {
              const merged = { ...prev };
              const t = data.data;
              if (t.instrument_token) {
                merged[t.instrument_token] = { ...merged[t.instrument_token], ...t };
              }
              return merged;
            });
          } else if (data.type === 'ticks' && Array.isArray(data.data)) {
            setTicks(prev => {
              const merged = { ...prev };
              data.data.forEach(t => {
                if (t.instrument_token) {
                  merged[t.instrument_token] = { ...merged[t.instrument_token], ...t };
                }
              });
              return merged;
            });
          }
        } catch { /* ignore parse errors */ }
      };
      ws.onclose = () => {
        wsRef.current = null;
        // Reconnect after 5s
        reconnectTimer.current = setTimeout(connectWS, 5000);
      };
      ws.onerror = () => {
        // WS not supported on this EC2 — that's fine, HTTP polling continues
        ws.close();
      };
    } catch {
      // WebSocket not available — HTTP polling handles it
    }
  }, []);

  useEffect(() => {
    connectWS();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [connectWS]);

  /* ── Subscribe to instrument tokens ─────────────── */
  const subscribeTokens = useCallback((tokens) => {
    if (!tokens || !tokens.length) return;
    tokens.forEach(t => subscribedTokens.current.add(t));

    // Send via WebSocket if connected
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'subscribe',
        tokens: tokens,
      }));
    }

    // Also tell EC2 via HTTP to subscribe these tokens on KiteTicker
    fetch(EC2_URL + '/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tokens }),
    }).catch(() => { /* silent — subscription endpoint may not exist yet */ });
  }, []);

  const getLTP = useCallback((symbol) => ticks[symbol]?.ltp || 0, [ticks]);
  const getOI = useCallback((symbol) => ticks[symbol]?.oi || 0, [ticks]);
  const getChg = useCallback((symbol) => ticks[symbol]?.chg || 0, [ticks]);

  return (
    <LiveDataContext.Provider value={{ ticks, connected, getLTP, getOI, getChg, subscribeTokens }}>
      {children}
    </LiveDataContext.Provider>
  );
}

export function useLiveData() {
  return useContext(LiveDataContext);
}
